import { createTheme } from '@mui/material/styles'

const theme = createTheme({
	// Enhanced breakpoints for better responsive design
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 900,
			lg: 1200,
			xl: 1536,
			// Custom breakpoints for specific use cases
			compact: 480,
			wide: 1400
		}
	},

	// Use rem-based spacing for better scalability
	spacing: (factor) => `${0.25 * factor}rem`,

	// Responsive typography that scales smoothly
	typography: {
		fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',

		// Responsive font sizes using clamp()
		h1: {
			fontSize: 'clamp(1.75rem, 2rem + 1vw, 3rem)',
			fontWeight: 300,
			lineHeight: 1.2
		},
		h2: {
			fontSize: 'clamp(1.5rem, 1.75rem + 0.8vw, 2.5rem)',
			fontWeight: 300,
			lineHeight: 1.2
		},
		h3: {
			fontSize: 'clamp(1.25rem, 1.5rem + 0.6vw, 2rem)',
			fontWeight: 400,
			lineHeight: 1.3
		},
		h4: {
			fontSize: 'clamp(1.125rem, 1.25rem + 0.4vw, 1.75rem)',
			fontWeight: 400,
			lineHeight: 1.3
		},
		h5: {
			fontSize: 'clamp(1rem, 1.125rem + 0.3vw, 1.5rem)',
			fontWeight: 400,
			lineHeight: 1.4
		},
		h6: {
			fontSize: 'clamp(0.9rem, 1rem + 0.2vw, 1.25rem)',
			fontWeight: 500,
			lineHeight: 1.4
		},
		subtitle1: {
			fontSize: 'clamp(0.9rem, 1rem + 0.2vw, 1.125rem)',
			fontWeight: 400,
			lineHeight: 1.5
		},
		subtitle2: {
			fontSize: 'clamp(0.8rem, 0.875rem + 0.15vw, 1rem)',
			fontWeight: 500,
			lineHeight: 1.5
		},
		body1: {
			fontSize: 'clamp(0.875rem, 0.9rem + 0.2vw, 1rem)',
			lineHeight: 1.5
		},
		body2: {
			fontSize: 'clamp(0.75rem, 0.8rem + 0.15vw, 0.875rem)',
			lineHeight: 1.5
		},
		caption: {
			fontSize: 'clamp(0.6875rem, 0.75rem + 0.1vw, 0.8125rem)',
			lineHeight: 1.4
		},
		button: {
			fontSize: 'clamp(0.8125rem, 0.875rem + 0.1vw, 0.9375rem)',
			fontWeight: 500,
			textTransform: 'none' // More modern button styling
		}
	},

	// Enhanced component styling for better scaling
	components: {
		// List components optimized for scaling
		MuiListItem: {
			styleOverrides: {
				root: {
					padding: '0.75rem 1rem',
					'@media (max-width: 900px)': {
						padding: '1rem 0.75rem'
					},
					'@media (max-height: 600px)': {
						padding: '0.5rem 1rem'
					},
					'&.MuiListItem-dense': {
						padding: '0.5rem 1rem',
						'@media (max-width: 900px)': {
							padding: '0.75rem 0.75rem'
						}
					}
				}
			}
		},

		// Button components with proper touch targets
		MuiIconButton: {
			styleOverrides: {
				root: {
					padding: '0.5rem',
					'@media (max-width: 900px)': {
						padding: '0.75rem',
						minWidth: '44px',
						minHeight: '44px'
					},
					'&.MuiIconButton-sizeSmall': {
						padding: '0.25rem',
						'@media (max-width: 900px)': {
							padding: '0.5rem',
							minWidth: '40px',
							minHeight: '40px'
						}
					}
				}
			}
		},

		MuiButton: {
			styleOverrides: {
				root: {
					padding: '0.5rem 1rem',
					minHeight: '36px',
					'@media (max-width: 900px)': {
						padding: '0.75rem 1.25rem',
						minHeight: '44px'
					},
					'&.MuiButton-sizeSmall': {
						padding: '0.25rem 0.75rem',
						minHeight: '32px',
						'@media (max-width: 900px)': {
							padding: '0.5rem 1rem',
							minHeight: '40px'
						}
					}
				}
			}
		},

		// Chip component scaling
		MuiChip: {
			styleOverrides: {
				root: {
					fontSize: 'clamp(0.6875rem, 0.75rem + 0.1vw, 0.8125rem)',
					height: 'auto',
					padding: '0.25rem 0.5rem',
					'& .MuiChip-label': {
						padding: '0.125rem 0.25rem'
					},
					'& .MuiChip-icon': {
						fontSize: 'clamp(0.875rem, 1rem + 0.1vw, 1.125rem)'
					}
				},
				sizeSmall: {
					fontSize: 'clamp(0.625rem, 0.6875rem + 0.1vw, 0.75rem)',
					padding: '0.125rem 0.375rem'
				}
			}
		},

		// Typography components with better scaling
		MuiTypography: {
			styleOverrides: {
				root: {
					// Ensure text doesn't break layouts
					wordBreak: 'break-word',
					hyphens: 'auto'
				}
			}
		},

		// Stack component for consistent spacing
		MuiStack: {
			styleOverrides: {
				root: {
					'& > :not(style) + :not(style)': {
						marginTop: '0.5rem',
						'@media (max-width: 900px)': {
							marginTop: '0.75rem'
						}
					}
				}
			}
		},

		// Card components with responsive padding
		MuiCard: {
			styleOverrides: {
				root: {
					padding: 0,
					'& .MuiCardContent-root': {
						padding: '1rem',
						'@media (max-width: 900px)': {
							padding: '1.25rem'
						},
						'&:last-child': {
							paddingBottom: '1rem',
							'@media (max-width: 900px)': {
								paddingBottom: '1.25rem'
							}
						}
					}
				}
			}
		},

		// Dialog components for better mobile experience
		MuiDialog: {
			styleOverrides: {
				paper: {
					margin: '1rem',
					'@media (max-width: 900px)': {
						margin: '0.5rem'
					}
				}
			}
		},

		// TextField components with proper scaling
		MuiTextField: {
			styleOverrides: {
				root: {
					'& .MuiInputBase-root': {
						fontSize: 'clamp(0.875rem, 0.9rem + 0.1vw, 1rem)'
					},
					'& .MuiInputLabel-root': {
						fontSize: 'clamp(0.875rem, 0.9rem + 0.1vw, 1rem)'
					}
				}
			}
		},

		// Fab component for mobile
		MuiFab: {
			styleOverrides: {
				root: {
					'@media (max-width: 900px)': {
						width: '56px',
						height: '56px'
					}
				}
			}
		}
	},

	// Enhanced palette
	palette: {
		primary: {
			main: '#1976d2',
			light: '#42a5f5',
			dark: '#1565c0'
		},
		secondary: {
			main: '#dc004e',
			light: '#ff5983',
			dark: '#9a0036'
		},
		background: {
			default: '#fafafa',
			paper: '#ffffff'
		},
		text: {
			primary: 'rgba(0, 0, 0, 0.87)',
			secondary: 'rgba(0, 0, 0, 0.6)'
		},
		action: {
			hover: 'rgba(0, 0, 0, 0.04)',
			selected: 'rgba(0, 0, 0, 0.08)'
		}
	}
})

export default theme
